import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Contrast, Languages, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth/auth-context';
import { currentLocale } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { useChoosePreference } from '@/lib/preferences';
import { Button } from '@/components/ui/button';
import { ModuleSwitcher } from '@/components/layout/module-switcher';
import { OfflineBanner } from '@/components/layout/offline-banner';
import { ModuleBottomBar, ModuleNavBar, type ModuleNav } from '@/components/layout/module-nav';
import type { AppModule } from '@overseer/contracts';

/**
 * The page chrome, shared by every module.
 *
 * Skip link, the identity block, theme, language, sign-out, the offline banner
 * and the scroll container. **Not the navigation** — that is `ModuleNavBar`,
 * given a `ModuleNav` by whichever module the user is in.
 *
 * Overseer is several applications behind one sign-in, and each supplies its
 * own nav: the bar is full at nine items, and the hotel's screens are not HR's
 * screens plus more.
 */

export const AppShell = ({
  nav,
  modules,
  current,
  children,
}: {
  nav: ModuleNav;
  /** What this user may open. One module renders the mark, not a switcher. */
  modules: AppModule[];
  current: AppModule | null;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const { displayName, signOut } = useAuth();
  const theme = useTheme();
  const choose = useChoosePreference();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const cycleTheme = () => {
    choose.theme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system');
  };

  const toggleLocale = () => {
    choose.locale(currentLocale() === 'pl' ? 'en' : 'pl');
  };

  /*
   * `h-screen` with the main column scrolling itself, rather than `min-h-screen`
   * with the document scrolling. A screen that wants to fill the viewport (the
   * timesheet grid) can only measure "what is left" if something above it has a
   * definite height. `min-h-screen` gives neither, which is why that grid had to
   * guess with `calc(100vh - 19rem)` and guessed 16px wrong.
   *
   * `dvh`, not `vh`: on a phone `100vh` is the height with the browser's
   * toolbars hidden, so the bottom of the page sat under them.
   */
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-bg text-fg">
      {/*
        The skip link. Nav items plus three icon buttons stand between the top of
        the document and the screen's own content, and a keyboard user hits all of
        them on every navigation. Visible only when focused, which is the one case
        where it is the first thing in the tab order.
      */}
      <a
        href="#main"
        className="sr-only z-50 focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:rounded-md focus:bg-bg-raised focus:px-3 focus:py-2 focus:text-sm focus:shadow-lg focus:outline-2 focus:outline-ring"
      >
        {t('common.skipToContent')}
      </a>

      <header className="shrink-0 border-b border-border-default bg-bg-raised">
        <div className="flex h-12 items-center gap-3 px-3 sm:px-4">
          <ModuleSwitcher modules={modules} current={current} />

          <ModuleNavBar nav={nav} />

          <div className="flex shrink-0 items-center gap-1 border-l border-border-default pl-2 sm:pl-3">
            {/*
              The identity block is the way to the account screen — password,
              language, theme. A nav item of its own would put "Account" beside
              "Timesheet", which is not what it is.
            */}
            <Link
              to="/account"
              className="mr-1 hidden max-w-36 truncate rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg lg:block"
              title={displayName ?? t('account.title')}
              aria-current={pathname === '/account' ? 'page' : undefined}
            >
              {displayName ?? t('account.title')}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              aria-label={`${t('common.theme')}: ${t(
                theme === 'system'
                  ? 'common.themeSystem'
                  : theme === 'dark'
                    ? 'common.themeDark'
                    : 'common.themeLight',
              )}`}
              title={t('common.theme')}
            >
              <Contrast aria-hidden className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLocale}
              aria-label={t('common.language')}
              title={t('common.language')}
            >
              <Languages aria-hidden className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void signOut()}
              aria-label={t('auth.signOut')}
              title={t('auth.signOut')}
            >
              <LogOut aria-hidden className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <OfflineBanner />
      {/* tabIndex -1 so the skip link can actually put focus here; without it the
          browser scrolls to the anchor and leaves focus at the top of the page. */}
      {/*
        `p-4` on a phone, `p-6` from `sm`: 16 px more width where it is scarce.
        `relative`: a screen-reader-only label is absolute, and without a
        positioned ancestor inside the scrolling column it lengthens the
        document instead — a second scrollbar beside the column's own.

        On a phone the bottom padding is a spacer instead, as tall as the
        module's bottom tabs plus the gutter, so the last row clears the tabs
        while a screen's sticky action bar can still sit flush on them.
      */}
      <main
        id="main"
        tabIndex={-1}
        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-4 pb-0 sm:p-6"
      >
        {children}
        <div aria-hidden className="h-[calc(var(--bottom-nav-height,0px)_+_1rem)] shrink-0 sm:hidden" />
      </main>
      {nav.bottomOnPhone ? <ModuleBottomBar nav={nav} /> : null}
    </div>
  );
};
